import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { Row, Col } from "react-bootstrap";

import { FORM_FIELD_KEYS } from "../../../../constants/forms";
import { getFieldProps } from "../../../../utils/form-fields";
import { getFileExtension, getFileExtensionsString, checkIfFileAllowed } from "../../../../utils/file";

import TextInput from "../../../controls/TextInput";
import RadioGroup from "../../../controls/RadioGroup";
import Switch from "../../../controls/Switch";
import Select from "../../../controls/Select";
import Upload from "../../../controls/Upload";

const getYearOptions = () => {
  const startYear = new Date().getFullYear() - 24;

  return Array.from(new Array(26), (_, i) => {
    return { value: startYear + i, title: `${startYear + i}` };
  });
};

const markDescLabel = {
  NAME: "Name you wish to protect",
  SLOGAN: "Slogan you wish to protect",
  LOGO: "word(s)/letter(s)/number(s)",
};

const markDescPlaceholder = {
  NAME: "Enter the name you wish to protect",
  SLOGAN: "Enter the slogan you wish to protect",
  LOGO: "Enter the description of your logo",
};

const markDescDefaultInvalidMessage = {
  NAME: "You must provide the name",
  SLOGAN: "You must provide the slogan",
  LOGO: "You must provide the description",
};

const StyledRow = styled(Row)`
  .col-12 + .col-4 {
    padding-right: 0;
  }

  .col-12 + .col-4 + .col-4 {
    padding-left: 8px;
    padding-right: 8px;
  }

  .col-12 + .col-4 + .col-4 + .col-4 {
    padding-left: 0;
  }

  .col-12 + .col-4 + .col-4 + .col-4 + .col-4 {
    padding-right: 0;
  }

  .col-12 + .col-4 + .col-4 + .col-4 + .col-4 + .col-4 {
    padding-left: 8px;
    padding-right: 8px;
  }

  .col-12 + .col-4 + .col-4 + .col-4 + .col-4 + .col-4 + .col-4 {
    padding-left: 0;
  }
`;

function TrademarkGeneralInfoBlock({ values = {}, onChange }) {
  const [allowedExtensionsString, setAllowedExtensionsString] = useState("");
  const [allowedExtensionsArray, setAllowedExtensionsArray] = useState([]);
  const [logoFileInvalidMessage, setLogoFileInvalidMessage] = useState("");

  const handleLogoFileChange = (e) => {
    const { files } = e?.target || {};

    const fileExtension = getFileExtension(files[0]?.name);

    const isExtensionAllowed = checkIfFileAllowed(fileExtension, allowedExtensionsArray);

    setLogoFileInvalidMessage("");

    if (!isExtensionAllowed) {
      setLogoFileInvalidMessage(`Incorrect file type. Allowed ${allowedExtensionsString}`);
    } else {
      const fileSizeMb = files[0]?.size / 1024 / 1024;

      if (fileSizeMb >= 3.8) {
        setLogoFileInvalidMessage("File size exceeds 3.8 Mb");
      }
    }

    onChange && onChange(e);
  };

  useEffect(() => {
    let url = process.env.NEXT_PUBLIC_REACT_APP_API + "/category/products/TRADEMARK_RegisterTrademark/file-types";

    axios
      .get(url, {
        headers: { "Content-Type": "application/json" },
      })
      .then((response) => {
        if (response.status === 200) {
          response.data
            .filter((file) => file.code.includes("TRADEMARK_LOGO"))
            .forEach((allowedFile) => {
              setAllowedExtensionsString(getFileExtensionsString(allowedFile.allowedExtensions));
              setAllowedExtensionsArray(allowedFile.allowedExtensions);
            });
        }
      });
  }, []);

  return (
    <StyledRow>
      <Col xs={12}>
        <div className="inputs-block-title">
          <span>General Information</span>
        </div>
      </Col>
      <Col xs={12}>
        <div className="input-title">
          <strong>Select what you are trying to protect</strong>
        </div>
      </Col>
      <Col xs={12}>
        <RadioGroup
          horizontal
          {...getFieldProps(FORM_FIELD_KEYS.trademarkType)}
          value={values[FORM_FIELD_KEYS.trademarkType]}
          onChange={onChange}
        />
      </Col>
      {values[FORM_FIELD_KEYS.trademarkType] === "LOGO" && (
        <>
          <Col xs={12}>
            <div className="input-title">
              <span>
                Please provide a complete and accurate description of your logo, including any words that may appear in
                the logo. An example of a description is: ‘a red cat wrapped around a blue outline of a globe’. Leave
                out the colors if you want to protect your logo in all colors.
              </span>
            </div>
          </Col>
          <Col xs={12}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.literalElements)}
              value={values[FORM_FIELD_KEYS.literalElements]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12}>
            <div className="input-title">
              <span>Do you want to be able to protect your logo in a variety of colors?</span>
            </div>
          </Col>
          <Col xs={12}>
            <Switch
              {...getFieldProps(FORM_FIELD_KEYS.logoColoredVariety)}
              value={values[FORM_FIELD_KEYS.logoColoredVariety]}
              onChange={onChange}
            />
          </Col>
          {values[FORM_FIELD_KEYS.logoColoredVariety] === "false" && (
            <Col xs={12}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.colorsInLogo)}
                value={values[FORM_FIELD_KEYS.colorsInLogo]}
                onChange={onChange}
              />
            </Col>
          )}
          <Col xs={12}>
            <div className="input-title">
              <span>Please provide any word(s), letter(s), and/or number(s) that appear in your logo</span>
            </div>
          </Col>
        </>
      )}
      <Col xs={12}>
        <TextInput
          {...getFieldProps(FORM_FIELD_KEYS.markDesc)}
          label={markDescLabel[values[FORM_FIELD_KEYS.trademarkType]]}
          placeholder={markDescPlaceholder[values[FORM_FIELD_KEYS.trademarkType]]}
          defaultInvalidMessage={markDescDefaultInvalidMessage[values[FORM_FIELD_KEYS.trademarkType]]}
          value={values[FORM_FIELD_KEYS.markDesc]}
          onChange={onChange}
        />
      </Col>
      {values[FORM_FIELD_KEYS.trademarkType] === "LOGO" && (
        <Col xs={12}>
          <Upload
            {...getFieldProps(FORM_FIELD_KEYS.logoFile)}
            defaultInvalidMessage={logoFileInvalidMessage || "Logo file is not chosen"}
            validated={values.validated}
            invalid={Boolean(logoFileInvalidMessage)}
            value={values[FORM_FIELD_KEYS.logoFile]}
            onChange={handleLogoFileChange}
          />
        </Col>
      )}
      <Col xs={12}>
        <div className="input-title">
          <span>
            Does your mark include a living person’s name—even if a stage name or pseudonym or could it be perceived to
            include a person’s name?
          </span>
        </div>
      </Col>
      <Col xs={12}>
        <Switch
          {...getFieldProps(FORM_FIELD_KEYS.isIncludeLivingPersonName)}
          value={values[FORM_FIELD_KEYS.isIncludeLivingPersonName]}
          onChange={onChange}
        />
      </Col>
      {values[FORM_FIELD_KEYS.isIncludeLivingPersonName] === "true" && (
        <>
          <Col xs={12}>
            <div className="input-title">
              <span>Does your mark include a name (including a pseudonym, nickname or stage name) that is yours?</span>
            </div>
          </Col>
          <Col xs={12}>
            <Switch
              {...getFieldProps(FORM_FIELD_KEYS.isIncludeNicknameName)}
              value={values[FORM_FIELD_KEYS.isIncludeNicknameName]}
              onChange={onChange}
            />
          </Col>
          {values[FORM_FIELD_KEYS.isIncludeNicknameName] === "true" && (
            <>
              <Col xs={12}>
                <div className="input-title">
                  <span>Enter the legal name to whom the mark references</span>
                </div>
              </Col>
              <Col xs={12}>
                <TextInput
                  {...getFieldProps(FORM_FIELD_KEYS.ownName)}
                  value={values[FORM_FIELD_KEYS.ownName]}
                  onChange={onChange}
                />
              </Col>
            </>
          )}
          {values[FORM_FIELD_KEYS.isIncludeNicknameName] === "false" && (
            <>
              <Col xs={12}>
                <div className="input-title">
                  <span>
                    Does your mark include the name (including a pseudonym, nickname or stage name) of a living person
                    other than yourself?
                  </span>
                </div>
              </Col>
              <Col xs={12}>
                <Switch
                  {...getFieldProps(FORM_FIELD_KEYS.isOtherLivingName)}
                  value={values[FORM_FIELD_KEYS.isOtherLivingName]}
                  onChange={onChange}
                />
              </Col>
              {values[FORM_FIELD_KEYS.isOtherLivingName] === "true" && (
                <>
                  <Col xs={12}>
                    <div className="input-title">
                      <span>What is the name of the living person in your mark?</span>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <TextInput
                      {...getFieldProps(FORM_FIELD_KEYS.ownName)}
                      label="Living Person Name"
                      placeholder="Enter the name of the living person"
                      defaultInvalidMessage="You must provide the name of the living person"
                      value={values[FORM_FIELD_KEYS.ownName]}
                      onChange={onChange}
                    />
                  </Col>
                </>
              )}
            </>
          )}
        </>
      )}
      <Col xs={12}>
        <div className="input-title">
          <span>Does your mark include any words other than English or non-Latin characters?</span>
        </div>
      </Col>
      <Col xs={12}>
        <Switch
          {...getFieldProps(FORM_FIELD_KEYS.includeTranslationWords)}
          value={values[FORM_FIELD_KEYS.includeTranslationWords]}
          onChange={onChange}
        />
      </Col>
      {values[FORM_FIELD_KEYS.includeTranslationWords] === "true" && (
        <>
          <Col xs={12}>
            <div className="input-title">
              <span>
                Please provide the English translation or transliteration of any non-Latin characters or non-English
                words
              </span>
            </div>
          </Col>
          <Col xs={12}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.translation)}
              value={values[FORM_FIELD_KEYS.translation]}
              onChange={onChange}
            />
          </Col>
        </>
      )}
      <Col xs={12}>
        <div className="input-title">
          <span>Are you currently using this trademark in your business?</span>
        </div>
      </Col>
      <Col xs={12}>
        <Switch
          {...getFieldProps(FORM_FIELD_KEYS.currentTrademark)}
          value={values[FORM_FIELD_KEYS.currentTrademark]}
          onChange={onChange}
        />
      </Col>
      {values[FORM_FIELD_KEYS.currentTrademark] === "false" && (
        <Col xs={12}>
          <div className="input-title">
            <span>
              If you do not have proof now, you can submit proof later after your trademark is submitted to the
              Trademark office. If you do it later, you will need to pay a $150 government fee (for US trademarks).
            </span>
          </div>
        </Col>
      )}
      {values[FORM_FIELD_KEYS.currentTrademark] === "true" && (
        <>
          <Col xs={4}>
            <Select
              {...getFieldProps(FORM_FIELD_KEYS.firstUseAnywhereMonth)}
              value={values[FORM_FIELD_KEYS.firstUseAnywhereMonth]}
              validated={values.validated}
              onChange={onChange}
            />
          </Col>
          <Col xs={4}>
            <Select
              {...getFieldProps(FORM_FIELD_KEYS.firstUseAnywhereDay)}
              value={values[FORM_FIELD_KEYS.firstUseAnywhereDay]}
              validated={values.validated}
              onChange={onChange}
            />
          </Col>
          <Col xs={4}>
            <Select
              {...getFieldProps(FORM_FIELD_KEYS.firstUseAnywhereYear)}
              options={getYearOptions()}
              value={values[FORM_FIELD_KEYS.firstUseAnywhereYear]}
              validated={values.validated}
              onChange={onChange}
            />
          </Col>
          <Col xs={4}>
            <Select
              {...getFieldProps(FORM_FIELD_KEYS.firstUseInCommerceMonth)}
              value={values[FORM_FIELD_KEYS.firstUseInCommerceMonth]}
              validated={values.validated}
              onChange={onChange}
            />
          </Col>
          <Col xs={4}>
            <Select
              {...getFieldProps(FORM_FIELD_KEYS.firstUseInCommerceDay)}
              value={values[FORM_FIELD_KEYS.firstUseInCommerceDay]}
              validated={values.validated}
              onChange={onChange}
            />
          </Col>
          <Col xs={4}>
            <Select
              {...getFieldProps(FORM_FIELD_KEYS.firstUseInCommerceYear)}
              options={getYearOptions()}
              value={values[FORM_FIELD_KEYS.firstUseInCommerceYear]}
              validated={values.validated}
              onChange={onChange}
            />
          </Col>
        </>
      )}
    </StyledRow>
  );
}

export default TrademarkGeneralInfoBlock;
